/**
 * TextLink — shared aspect. The amber uppercase label + optional
 * trailing arrow CTA-link pattern used across the app. Two tones:
 *   accent     — theme.accent (default; brighter amber, for CTA rails)
 *   accentDeep — theme.accentDeep (muted amber, for in-card links)
 *
 * Pressable. Arrow renders inline as a small SVG matching the label
 * color so it tints with the tone.
 *
 * brand-aspect: consumed by Student portal (offers screen "Show more"/
 * "Show less" toggle on long interview-offer messages).
 */
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

import type { PaneTheme } from "../brand/paneThemes";

export type TextLinkTone = "accent" | "accentDeep";

export type TextLinkProps = {
  theme: PaneTheme;
  label: string;
  onPress: () => void;
  /** Default "accent" (theme.accent). Use "accentDeep" for in-card links. */
  tone?: TextLinkTone;
  /** Default true. Set false to render the label without the trailing arrow. */
  showArrow?: boolean;
  accessibilityLabel?: string;
};

export function TextLink({
  theme,
  label,
  onPress,
  tone = "accent",
  showArrow = true,
  accessibilityLabel,
}: TextLinkProps) {
  const isWeb = Platform.OS === "web";
  const color = tone === "accentDeep" ? theme.accentDeep : theme.accent;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      style={[
        { flexDirection: "row", alignItems: "center", gap: 6 },
        isWeb ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
      {showArrow ? (
        <Svg width={10} height={10} viewBox="0 0 12 12">
          <Path
            d="M2 6 H10 M7 3 L10 6 L7 9"
            stroke={color}
            strokeWidth={1.6}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      ) : null}
    </Pressable>
  );
}
